import { useMemo, useState } from "react";
import { bool, number, object, func, oneOfType, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MAX_WORK_TYPE_LENGTH } from "@repo/utils";
import { useEditorWebsocket } from "@hooks";
import {
  useEstimateStructure,
  useEstimateEditorStore,
  useEstimateEditorSettings,
  useEstimateTableSettings,
} from "@tools";
import { Alert, AsyncPicker, Tag } from "@components";
import { DifferentSalesRateActionAlert } from "@tools/EstimateEditor/dialogs/DifferentSalesRateActionAlert";

export const WorkTypeManager = observer(({
  open,
  section,
  onClose,
  anchorEl,
  sectionWorkTypeId,
}) => {
  const { t } = useTranslation();
  const { isLibrary } = useEstimateEditorSettings();
  const { visibleWorkTypes } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();

  const [ removedWorkTypeId, setRemovedWorkType ] = useState(null);
  const [ newWorkTypeData, setNewWorkTypeData ] = useState(null);
  
  const editorMode = useMemo(() => (
    Boolean(sectionWorkTypeId)
  ), [ open ]);

  const selectedWorkTypes = useMemo(() => (
    visibleWorkTypes.map(wT => ({ ...wT, id: wT.systemId ?? wT.id }))
  ), [ visibleWorkTypes ]);
  
  const confirmRemove = async () => {
    structure.historyManager.startGroup();
    const path = structure.getWorkTypeById(removedWorkTypeId).treePath.join("/");
    section.removeWorkType(removedWorkTypeId, true);
    const removed = structure.checkAndRemoveSectionWorkType(removedWorkTypeId);
    if(removed) {
      socket?.requestCommentPathRemove(path);
      socket?.requestCommentPathRemove("t"+path);
    }
    structure.historyManager.stopGroup();
    
    setRemovedWorkType(null);
  };

  const handleWorkTypeRemove = (removedWorkTypeId) => {
    if (isLibrary) {
      const wT = section.workTypes.find((wT) => wT.id === removedWorkTypeId);
      // wT?.setBreakdownVisibility(!wT?.inBreakdown);
      structure.setWorkTypeVisibility?.(wT.id, !wT.invisible, true);
      return;
    }
    setRemovedWorkType(removedWorkTypeId);
  };
  
  const handleRateAction = (workType) => {
    updateOrAddWorkType(workType)
    setNewWorkTypeData(null);
  }

  const updateOrAddWorkType = async (workType, custom=false) => {
    structure.historyManager.startGroup();
    
    const structureWorkTypeId = await checkStructureWorkType(workType);

    if(custom)
      editorStore.addCustomWorkType(structureWorkTypeId, workType.name);
    
    if(editorMode) {
      section.changeWorkType(sectionWorkTypeId, structureWorkTypeId, true, false, true);
      if (isLibrary) {
        structure.removeWorkType(sectionWorkTypeId);
      }
      onClose();
    } else
      section.addWorkType(structureWorkTypeId)
      // structure.addWorkTypeToSections(structureWorkTypeId, section.id);
    structure.historyManager.stopGroup();
  };

  const checkStructureWorkType = async (workType) => {
    const existingWorkType = typeof workType.id === "number"
      ? structure.getWorkTypeBySystemId(workType.id)
      : structure.getWorkTypeById(workType.id);

    if (existingWorkType)
      return existingWorkType.id;

    const { id: systemId, salesRate, costRate, ...data } = workType;
    const id = structure.addWorkType(
      false,
      {
        ...data,
        id: typeof systemId === "number" ? systemId : undefined,
      },
      salesRate,
      costRate,
    );
    
    editorStore?.addWorkTypeWithStatus(id);
    return id;
  };

  const createCustomWorkType = async (name) => {
    await updateOrAddWorkType({ name }, true);
  };

  const disableOption = (option) => {
    if(!sectionWorkTypeId) return false;

    const workType = typeof option.id === "number"
      ? structure.getWorkTypeBySystemId(option.id)
      : structure.getWorkTypeById(option.id);

    return Boolean(workType && section.usedWorkTypes.includes(workType.id));
  };

  return (
    <AsyncPicker
      isOpen={open}
      inputName="tag-search"
      selected={selectedWorkTypes}
      anchorEl={anchorEl}
      inputLabel={t("views.active.search_tag")}
      ContentItem={({ children, backgroundColor}) => (
        <Tag text={children} color={backgroundColor} />
      )}
      title={t(
        `views.editor.tags_edition${
          sectionWorkTypeId
            ? "_change"
            : isLibrary
            ? "_library"
            : ""
        }`
      )}
      options={editorStore?.workTypes.slice()}
      onClickAway={onClose}
      onAdd={setNewWorkTypeData}
      onRemove={handleWorkTypeRemove}
      disableOptions={disableOption}
      newItemText={(value) => t("views.editor.custom_workType", { value })}
      noItemsText={t("views.editor.no_work_types")}
      onNewItemAdd={createCustomWorkType}
      allowNewItemCreation={!isLibrary}
      maxLength={MAX_WORK_TYPE_LENGTH}
    >
      <Alert
        isOpen={Boolean(removedWorkTypeId)}
        title={t(
          `alerts.editor.${
            isLibrary
              ? "tag_remove_library"
              : "tag_remove_section"
          }`
        )}
        onAccept={confirmRemove}
        onCancel={() => setRemovedWorkType(null)}
        acceptText={t("common.remove")}
        cancelText={t("common.cancel")}
      />
      {
        Boolean(newWorkTypeData) &&
        <DifferentSalesRateActionAlert
          onAccept={handleRateAction}
          onCancel={() => setNewWorkTypeData(null)}
          newElementData={newWorkTypeData}
        />
      }
    </AsyncPicker>
  );
});

WorkTypeManager.propTypes = {
  show: bool,
  anchorEl: object,
  section: object.isRequired,
  onClose: func.isRequired,
  sectionWorkTypeId: oneOfType([number, string]),
};
